import ProductComparisonBrand, {
  ComparisonBrandPrice,
} from '@src/quince-site-common/src/domains/ProductComparisonBrand'
import { IProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { useCallback } from 'react'
import ProductPriceComparison from '../../../molecules/productPriceComparision'

import { getDiscountedPrice } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import Product from '@src/quince-site-common/src/domains/Product'
import formatDisplayAmountValue from '@src/quince-site-common/src/utils/formatDisplayAmountValue'
import BundlePageSectionProps from '@src/quince-site-pdp/src/domains/BundlePageSectionProps'
import { getProductPriceMap } from '../bundleProductPurchase/BundleProductPurchase.helpers'
import { getBrandComparisonsForDevice, getProductComparisionBrands } from './BrandComparison'
import styles from './brandComparision.module.scss'

const BrandComparisonBundle = ({ bundle }: BundlePageSectionProps) => {
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType !== DEVICE_TYPES.DESKTOP

  const discount = bundle.discountPrice || 0

  const bundleSelectedOptions = useAppSelector<{ product: Product; selectedOptions: IProductVariantOption[] }[]>(
    pdpStateManager,
    pdpStateManager.getBundleSelectedOptions,
  )
  const prices = bundleSelectedOptions.length ? getProductPriceMap(bundleSelectedOptions) : undefined
  const productsSumPrice =
    prices &&
    formatDisplayAmountValue(
      prices.reduce((sum, { product, pricing }) => sum + parseFloat(pricing.formattedSalePrice), 0),
    )
  const quincePrice = getDiscountedPrice(Number(productsSumPrice), discount)

  const productComparisionsBrands = (() => {
    const brandComparisonMap = new Map<string, { brandDetails: ProductComparisonBrand; count: number }>()

    bundleSelectedOptions.forEach(({ product, selectedOptions }) => {
      const productComparisionsBrands: ProductComparisonBrand[] = getProductComparisionBrands(
        product,
        selectedOptions,
        isMobile,
        true,
      )
      productComparisionsBrands.forEach(brandComparison => {
        const oldBrandComparisonDetails = brandComparisonMap.get(brandComparison.brand)
        const minPrice = getMinPricingForBrand(brandComparison.prices)
        let updatedPricing: ComparisonBrandPrice[] = brandComparison.prices
        let newCount = oldBrandComparisonDetails?.count || 0
        if (minPrice) {
          updatedPricing = [
            {
              price: (oldBrandComparisonDetails?.brandDetails.prices[0].price || 0) + minPrice.price,
              variantId: minPrice.variantId,
              updatedAt: minPrice.updatedAt,
            },
          ]
          newCount += 1
        }
        brandComparisonMap.set(brandComparison.brand, {
          brandDetails: {
            ...brandComparison,
            prices: updatedPricing,
          },
          count: newCount,
        })
      })
    })

    const _productComparisionsBrands: ProductComparisonBrand[] = []

    brandComparisonMap.forEach(({ brandDetails, count }) => {
      if (count === bundleSelectedOptions.length) {
        _productComparisionsBrands.push(brandDetails)
      }
    })

    return _productComparisionsBrands
  })()

  const getIsVisiblePriceCompareTrackParams = useCallback(() => {
    const productId = bundle.id
    return {
      action: 'brand_comparision',
      value: true,
      context: {
        productId,
      },
    }
  }, [bundle.id])

  const { ref: trackIsVisiblePriceCompareRef } = useTrackIsElementVisible(getIsVisiblePriceCompareTrackParams)

  if (productComparisionsBrands.length === 0 || !!bundle.hideBrandComparison) return null

  const filteredComparisonBrands = getBrandComparisonsForDevice(productComparisionsBrands, isMobile)
  return (
    <div className={styles.brandComparison} ref={trackIsVisiblePriceCompareRef}>
      <ProductPriceComparison
        quincePrice={+quincePrice}
        productComparisionsBrands={filteredComparisonBrands}
        hideFreeReturns={false}
        hasRestockingFee={bundle?.hasRestockingFee || undefined}
        showDollarComparisonPrice={false}
      />
    </div>
  )
}

const getMinPricingForBrand = (comparisonBrandPrices: ComparisonBrandPrice[]) => {
  if (!comparisonBrandPrices.length) {
    return null
  }
  const minPrice = comparisonBrandPrices.reduce((prev, curr) => (prev.price < curr.price ? prev : curr))
  return minPrice
}

export default BrandComparisonBundle
